import React, { useState, useRef } from 'react';
import { Image, X } from 'lucide-react';
import { useFirebase } from '../contexts/FirebaseContext';
import { db, storage } from '../config/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getInitials } from '../utils/getInitials';

const CreatePost = () => {
  const [content, setContent] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { currentUser } = useFirebase();

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadToCloudinary = async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('files', file);

    const API_URL = 'https://news-feed-api.likithyadavgn.workers.dev';
    
    try {
      console.log('Uploading file to server...');
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('Upload failed:', errorData);
        throw new Error(errorData?.error || 'Failed to upload image');
      }

      const data = await response.json();
      console.log('Upload response:', data);

      if (!data.urls || !data.urls[0]) {
        console.error('Invalid response format:', data);
        throw new Error('Invalid response from server');
      }

      return {
        url: data.urls[0]
      };
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    try {
      setIsSubmitting(true);

      let imageData = null;
      if (image) {
        imageData = await uploadToCloudinary(image);
      }

      const post = {
        content,
        author: {
          id: currentUser.uid,
          username: currentUser.username || currentUser.email?.split('@')[0],
          displayName: currentUser.displayName || currentUser.email?.split('@')[0],
        },
        image: imageData,
        createdAt: serverTimestamp(),
        likes: 0,
        comments: []
      };

      await addDoc(collection(db, 'posts'), post);

      // Reset form
      setContent('');
      setImage(null);
      setImagePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error creating post:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentUser) return null;

  return (
    <div className="border-b border-gray-800 p-4">
      <form onSubmit={handleSubmit}>
        <div className="flex space-x-4">
          {/* User Avatar */}
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center text-white font-medium">
              {getInitials(currentUser.displayName || currentUser.email?.split('@')[0] || '')}
            </div>
          </div>

          {/* Input Area */}
          <div className="flex-1">
            <textarea
              className="w-full bg-transparent border-none focus:ring-0 text-lg placeholder-gray-500 min-h-[100px]"
              placeholder="What's happening?"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              disabled={isSubmitting}
            />

            {/* Image Preview */}
            {imagePreview && (
              <div className="relative mt-2 rounded-lg overflow-hidden">
                <img
                  src={imagePreview}
                  alt="Upload preview"
                  className="max-h-96 w-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-2 right-2 p-2 bg-gray-900/80 rounded-full hover:bg-gray-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center space-x-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={handleImageClick}
                  className="p-2 text-purple-500 hover:bg-purple-500/10 rounded-full"
                  disabled={isSubmitting}
                >
                  <Image className="w-5 h-5" />
                </button>
              </div>

              <button
                type="submit"
                className={`px-4 py-2 bg-purple-500 text-white font-medium rounded-full hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed ${
                  !content.trim() ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={!content.trim() || isSubmitting}
              >
                {isSubmitting ? 'Posting...' : 'Post'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreatePost;
